import { useEffect, useState } from "react";
import apiClient from "../api/apiClient";
import PageTitle from "../components/common/PageTitle";
import AddButton from "../components/common/AddButton";
import AddItemModal from "../components/modals/AddItemModal";
import DataTable from "../components/common/DataTable";
import { EditableProjectRow } from "../components/table/EditableRow";
import { confirmDelete } from "../utils/confirm";
import { sanitizeInput } from "../utils/sanitize";
import {
  toastCreate,
  toastDelete,
  toastUpdate,
  showError,
  showWarning,
} from "../utils/toast";
import {
  STORAGE_KEYS,
  CACHE_DURATION,
  saveToSessionStorage,
  getFromSessionStorage,
  invalidateCache,
} from "../utils/sessionStorage";
import { ROLES } from "../config/constants";
import { useCurrentUser } from "../hooks/useCurrentUser";

export default function Project() {
  const { role } = useCurrentUser();

  // 🔥 FIX ROLE CASE
  const isProjectAdmin =
    role?.toLowerCase() === ROLES.PROJECT_ADMIN?.toLowerCase();

  const canAddProject = [ROLES.SUPER_ADMIN, ROLES.PROJECT_ADMIN].some(
    (r) => r.toLowerCase() === role?.toLowerCase()
  );

  const canEditProject = canAddProject;

  const canDeleteProject = [ROLES.SUPER_ADMIN, ROLES.PROJECT_ADMIN].some(
    (r) => r.toLowerCase() === role?.toLowerCase()
  );

  const [projects, setProjects] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);

  const [newProjectName, setNewProjectName] = useState("");
  const [newDivisionId, setNewDivisionId] = useState("");

  useEffect(() => {
    fetchProjects();
    fetchDivisions();
  }, []);

  async function fetchDivisions() {
    try {
      const res = await apiClient.get("/divisions");
      setDivisions(res.data);
    } catch {
      showError("Gagal memuat divisi");
    }
  }

  async function fetchProjects() {
    const cached = getFromSessionStorage(
      STORAGE_KEYS.PROJECTS,
      CACHE_DURATION.PROJECTS
    );

    if (cached) {
      setProjects(cached);
      setLoading(false);
      return;
    }

    try {
      const res = await apiClient.get("/projects");
      setProjects(res.data);
      saveToSessionStorage(STORAGE_KEYS.PROJECTS, res.data);

      if (isProjectAdmin && res.data?.length > 0) {
        const divId = res.data[0]?.division_id;
        if (divId) setNewDivisionId(divId.toString());
      }
    } catch {
      showError("Gagal memuat proyek");
    } finally {
      setLoading(false);
    }
  }

  async function handleAddProject() {
    if (!newProjectName.trim()) return showWarning("Nama wajib!");

    if (!isProjectAdmin && !newDivisionId) {
      return showWarning("Pilih divisi!");
    }

    try {
      await toastCreate(
        apiClient.post("/projects", {
          project: sanitizeInput(newProjectName),
          ...(isProjectAdmin
            ? {}
            : { division_id: parseInt(newDivisionId) }),
        }),
        newProjectName
      );

      setShowAddModal(false);
      invalidateCache([STORAGE_KEYS.PROJECTS, STORAGE_KEYS.PROJECT_NAMES]);
      fetchProjects();

      setNewProjectName("");
      setNewDivisionId("");
    } catch {
      showError("Gagal tambah project");
    }
  }

  async function updateProject(id, updatedData) {
    if (!updatedData.project.trim()) {
      return showWarning("Nama wajib diisi!");
    }

    try {
      await toastUpdate(
        apiClient.put(`/projects/${id}`, {
          project: sanitizeInput(updatedData.project),
          ...(isProjectAdmin
            ? {}
            : {
                division_id: updatedData.division_id
                  ? parseInt(updatedData.division_id)
                  : null,
              }),
        }),
        updatedData.project
      );

      invalidateCache([STORAGE_KEYS.PROJECTS, STORAGE_KEYS.PROJECT_NAMES]);
      fetchProjects();
    } catch (err) {
      console.log(err.response?.data); // 🔥 debug
      showError("Gagal update project");
    }
  }

  async function deleteProject(id) {
    const project = projects.find((p) => p.id === id);

    confirmDelete(project?.project, async () => {
      try {
        await toastDelete(
          apiClient.delete(`/projects/${id}`),
          project?.project
        );
        invalidateCache([STORAGE_KEYS.PROJECTS, STORAGE_KEYS.PROJECT_NAMES]);
        fetchProjects();
      } catch (err) {
        console.log(err.response?.data);
        showError("Gagal delete project");
      }
    });
  }

  return (
    <div className="p-6 pt-20">
      <div className="flex justify-between mb-5">
        <PageTitle>Manage Projects</PageTitle>
        {canAddProject && (
          <AddButton onClick={() => setShowAddModal(true)} />
        )}
      </div>

      <p className="mb-4">
        Total: <b>{projects.length}</b> proyek
      </p>

      <DataTable
        columns={[
          { field: "index", label: "No" },
          { field: "project", label: "Nama Proyek" },
          { field: "division_name", label: "Divisi" },
          ...(canEditProject ? [{ field: "actions", label: "Actions" }] : []),
        ]}
        data={projects}
        loading={loading}
        renderRow={(p, i) => (
          <EditableProjectRow
            key={p.id}
            project={p}
            index={i}
            divisions={divisions}
            onSave={updateProject}
            onDelete={deleteProject}
            showActions={canEditProject}
            canDelete={canDeleteProject}
            isProjectAdmin={isProjectAdmin}
          />
        )}
      />

      <AddItemModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleAddProject}
        title="Tambah Proyek"
      >
        <div className="space-y-4">
          {isProjectAdmin ? (
            <input
              disabled
              value="Divisi mengikuti akun Anda"
              className="w-full border px-3 py-2 bg-gray-100"
            />
          ) : (
            <select
              value={newDivisionId}
              onChange={(e) => setNewDivisionId(e.target.value)}
              className="w-full border px-3 py-2"
            >
              <option value="">Pilih Divisi</option>
              {divisions.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          )}

          <input
            value={newProjectName}
            onChange={(e) => setNewProjectName(e.target.value)}
            placeholder="Nama Project"
            className="w-full border px-3 py-2"
          />
        </div>
      </AddItemModal>
    </div>
  );
}
